import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://crmapi.epoptique.ma/api';
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';


// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
      }
    }
    console.log('API Request:', {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      data: config.data,
      headers: config.headers
    });
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('API Response Error:', {
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data,
      message: error.message
    });
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  createEmployee: (employeeData) => api.post('/auth/employees', employeeData),
  getEmployees: () => api.get('/auth/employees'),
  getEmployee: (id) => api.get(`/auth/employees/${id}`),
  updateEmployee: (id, employeeData) => api.put(`/auth/employees/${id}`, employeeData),
  deleteEmployee: (id) => api.delete(`/auth/employees/${id}`),
  // Store management (Admin only)
  getStores: () => api.get('/auth/stores'),
  getMyStore: () => api.get('/auth/stores/my-store'),
  createStore: (storeData) => api.post('/auth/stores', storeData),
  updateStore: (id, storeData) => api.put(`/auth/stores/${id}`, storeData),
};

// Page Privileges API
export const pagePrivilegesAPI = {
  getAllPrivileges: () => api.get('/page-privileges'),
  getPrivilegesByRole: (role) => api.get(`/page-privileges/role/${role}`),
  getPrivilegesBySection: (section) => api.get(`/page-privileges/section/${section}`),
  getSections: () => api.get('/page-privileges/sections'),
  createPrivilege: (privilegeData) => api.post('/page-privileges', privilegeData),
  updatePrivilege: (id, privilegeData) => api.put(`/page-privileges/${id}`, privilegeData),
  deletePrivilege: (id) => api.delete(`/page-privileges/${id}`),
  bulkUpdatePrivileges: (role, privileges) => api.put(`/page-privileges/role/${role}/bulk`, { privileges }),
  getMyPrivileges: () => api.get('/page-privileges/my-privileges'),
};

// Conventions (internes) — CRUD admin/manager, lecture tous
export const conventionsAPI = {
  list: () => api.get('/conventions'),
  get: (id) => api.get(`/conventions/${id}`),
  create: (data) => api.post('/conventions', data),
  update: (id, data) => api.put(`/conventions/${id}`, data),
  remove: (id) => api.delete(`/conventions/${id}`),
};

/** Annuaire contacts employés (nom, contact, description, téléphone, email optionnel) */
export const employeeContactsAPI = {
  list: () => api.get('/employee-contacts'),
  create: (data) => api.post('/employee-contacts', data),
  update: (id, data) => api.put(`/employee-contacts/${id}`, data),
  remove: (id) => api.delete(`/employee-contacts/${id}`),
};

/** Notes partagées par magasin (SAV, traçabilité, etc.) */
export const teamNotesAPI = {
  list: (params) => api.get('/team-notes', { params }),
  create: (data) => api.post('/team-notes', data),
  update: (id, data) => api.put(`/team-notes/${id}`, data),
  updateStatus: (id, data) => api.patch(`/team-notes/${id}/status`, data),
  remove: (id) => api.delete(`/team-notes/${id}`),
};

/** Tâches par magasin (assignation employé, chrono début/fin) */
export const todosAPI = {
  list: (params) => api.get('/todos', { params }),
  /** Tâches assignées à l’utilisateur connecté, non démarrées (alertes layout) */
  pendingAssignment: () => api.get('/todos/pending-assignment'),
  create: (data) => api.post('/todos', data),
  update: (id, data) => api.put(`/todos/${id}`, data),
  remove: (id) => api.delete(`/todos/${id}`),
  start: (id) => api.patch(`/todos/${id}/start`),
  finish: (id) => api.patch(`/todos/${id}/finish`),
  cancel: (id) => api.patch(`/todos/${id}/cancel`),
  pause: (id) => api.patch(`/todos/${id}/pause`),
  resume: (id) => api.patch(`/todos/${id}/resume`),
};

// Products API
export const productsAPI = {
  getProducts: (params) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  createProduct: (productData) => api.post('/products', productData),
  updateProduct: (id, productData) => api.put(`/products/${id}`, productData),
  deleteProduct: (id) => api.delete(`/products/${id}`),
  updateStock: (id, stock) => api.patch(`/products/${id}/stock`, { stock }),
  checkAvailability: (id) => api.get(`/products/${id}/availability`),
  checkAvailabilityBySearch: (params) => api.get('/products/availability/search', { params }),
  exportProducts: () => api.get('/products/export', { responseType: 'blob' }),
  importProducts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Stock Movements API
export const stockMovementsAPI = {
  getStockMovements: (params) => api.get('/stock-movements', { params }),
  getProductStockMovements: (productId) => api.get(`/stock-movements/product/${productId}`),
  createStockMovement: (movementData) => api.post('/stock-movements', movementData),
  bulkTransfer: (transferData) => api.post('/stock-movements/bulk-transfer', transferData),
  deleteStockMovement: (id) => api.delete(`/stock-movements/${id}`),
  exportStockMovements: (params) => api.get('/stock-movements/export', { 
    params, 
    responseType: 'blob' 
  }),
  getStockMovementStats: (params) => api.get('/stock-movements/stats', { params }),
};

// Sales API
export const salesAPI = {
  getSales: (params) => api.get('/sales', { params }),
  getSale: (id) => api.get(`/sales/${id}`),
  createSale: (saleData) => api.post('/sales', saleData),
  deleteSale: (id) => api.delete(`/sales/${id}`),
  getSalesStats: (params) => api.get('/sales/stats/summary', { params }),
};

// Purchases API
export const purchasesAPI = {
  getPurchases: (params) => api.get('/purchases', { params }),
  getPurchase: (id) => api.get(`/purchases/${id}`),
  createPurchase: (purchaseData) => api.post('/purchases', purchaseData),
  deletePurchase: (id) => api.delete(`/purchases/${id}`),
  getPurchaseStats: (params) => api.get('/purchases/stats/summary', { params }),
};

// Stores API
export const storesAPI = {
  getStores: () => api.get('/stores'),
  getStore: (id) => api.get(`/stores/${id}`),
  createStore: (storeData) => api.post('/stores', storeData),
  updateStore: (id, storeData) => api.put(`/stores/${id}`, storeData),
  deleteStore: (id) => api.delete(`/stores/${id}`),
  getStoreStats: (id) => api.get(`/stores/${id}/stats`),
};

// Suppliers API
export const suppliersAPI = {
  getSuppliers: () => api.get('/suppliers'),
  getSupplier: (id) => api.get(`/suppliers/${id}`),
  createSupplier: (supplierData) => api.post('/suppliers', supplierData),
  updateSupplier: (id, supplierData) => api.put(`/suppliers/${id}`, supplierData),
  deleteSupplier: (id) => api.delete(`/suppliers/${id}`),
  getSupplierPurchases: (id) => api.get(`/suppliers/${id}/purchases`),
};

// Clients API
export const clientsAPI = {
  getClients: () => api.get('/clients'),
  getClient: (id) => api.get(`/clients/${id}`),
  createClient: (clientData) => api.post('/clients', clientData),
  updateClient: (id, clientData) => api.put(`/clients/${id}`, clientData),
  deleteClient: (id) => api.delete(`/clients/${id}`),
};

// Attendance API
export const attendanceAPI = {
  getAttendance: () => api.get('/attendance'),
  getMyTodayAttendance: () => api.get('/attendance/me/today'),
  checkIn: () => api.post('/attendance/check-in'),
  checkOut: (data) => api.post('/attendance/check-out', data),
  createAttendance: (data) => api.post('/attendance', data),
  updateAttendance: (id, data) => api.put(`/attendance/${id}`, data),
  deleteAttendance: (id) => api.delete(`/attendance/${id}`),
};

// Promotions API
export const promotionsAPI = {
  getPromotions: () => api.get('/promotions'),
  getPromotion: (id) => api.get(`/promotions/${id}`),
  createPromotion: (promotionData) => api.post('/promotions', promotionData),
  updatePromotion: (id, promotionData) => api.put(`/promotions/${id}`, promotionData),
  deletePromotion: (id) => api.delete(`/promotions/${id}`),
};

// Credits API
export const creditsAPI = {
  getCredits: () => api.get('/credits'),
  getCreditsByClient: (clientId) => api.get(`/credits/client/${clientId}`),
  getCredit: (id) => api.get(`/credits/${id}`),
  createCredit: (creditData) => api.post('/credits', creditData),
  updateCredit: (id, creditData) => api.put(`/credits/${id}`, creditData),
  deleteCredit: (id) => api.delete(`/credits/${id}`),
  createAutoCredit: (creditData) => api.post('/credits/auto', creditData),
  reduceCredit: (creditData) => api.post('/credits/reduce', creditData),
  exportCredits: () => api.get('/credits/export', { responseType: 'blob' }),
};

// Sales Invoices API
export const salesInvoicesAPI = {
  getSalesInvoices: () => api.get('/sales-invoices'),
  getSalesInvoice: (id) => api.get(`/sales-invoices/${id}`),
  createSalesInvoice: (invoiceData) => api.post('/sales-invoices', invoiceData),
  updateSalesInvoice: (id, invoiceData) => api.put(`/sales-invoices/${id}`, invoiceData),
  deleteSalesInvoice: (id) => api.delete(`/sales-invoices/${id}`),
};

// Sales Orders API
export const salesOrdersAPI = {
  getSalesOrders: (params = {}) => api.get('/sales-orders', { params }),
  /** Liste paginée : 1 page sans recherche, toutes les pages avec `search`. */
  getSalesOrdersList: async (params = {}) => {
    const all = [];
    let page = 1;
    let pages = 1;
    const search = params.search != null ? String(params.search).trim() : '';
    do {
      const res = await api.get('/sales-orders', {
        params: { limit: 500, page, ...params },
      });
      const payload = res.data;
      const batch = Array.isArray(payload) ? payload : (payload?.data || []);
      all.push(...batch);
      pages = Number(payload?.pages) || 1;
      page += 1;
    } while (search && page <= pages);
    return all;
  },
  /** All pages, lightweight rows (for dropdowns — no items/payments per order). */
  getAllSalesOrdersForSelect: async (params = {}) => {
    const all = [];
    let page = 1;
    let pages = 1;
    do {
      const res = await api.get('/sales-orders', {
        params: { limit: 500, minimal: 1, page, ...params },
      });
      const payload = res.data;
      const batch = Array.isArray(payload) ? payload : (payload?.data || []);
      all.push(...batch);
      pages = Number(payload?.pages) || 1;
      page += 1;
    } while (page <= pages);
    return all;
  },
  getDeletedSalesOrders: (params = {}) => api.get('/sales-orders/deleted', { params }),
  getSalesOrder: (id, params = {}) => api.get(`/sales-orders/${id}`, { params }),
  createSalesOrder: (orderData) => api.post('/sales-orders', orderData),
  updateSalesOrder: (id, orderData) => api.put(`/sales-orders/${id}`, orderData),
  patchVerreTracking: (id, data) => api.patch(`/sales-orders/${id}/verre-tracking`, data),
  patchSalesOrderStatus: (id, data) => api.patch(`/sales-orders/${id}/status`, data),
  deleteSalesOrder: (id) => api.delete(`/sales-orders/${id}`),
  restoreSalesOrder: (id) => api.patch(`/sales-orders/${id}/restore`),
  permanentlyDeleteSalesOrder: (id) => api.delete(`/sales-orders/${id}/permanent`),
  addSalesOrderItem: (orderId, itemData) => api.post(`/sales-orders/${orderId}/items`, itemData),
  updateSalesOrderItem: (orderId, itemId, itemData) => api.put(`/sales-orders/${orderId}/items/${itemId}`, itemData),
  deleteSalesOrderItem: (orderId, itemId) => api.delete(`/sales-orders/${orderId}/items/${itemId}`),
};

// Credit Notes API
export const creditNotesAPI = {
  getCreditNotes: () => api.get('/credit-notes'),
  getCreditNote: (id) => api.get(`/credit-notes/${id}`),
  createCreditNote: (creditNoteData) => api.post('/credit-notes', creditNoteData),
  updateCreditNote: (id, creditNoteData) => api.put(`/credit-notes/${id}`, creditNoteData),
  deleteCreditNote: (id) => api.delete(`/credit-notes/${id}`),
};

// Doctors API
export const doctorsAPI = {
  getDoctors: () => api.get('/doctors'),
  getDoctor: (id) => api.get(`/doctors/${id}`),
  createDoctor: (doctorData) => api.post('/doctors', doctorData),
  updateDoctor: (id, doctorData) => api.put(`/doctors/${id}`, doctorData),
  deleteDoctor: (id) => api.delete(`/doctors/${id}`),
};

// Quotes API
export const quotesAPI = {
  getQuotes: () => api.get('/quotes'),
  getQuote: (id) => api.get(`/quotes/${id}`),
  createQuote: (quoteData) => api.post('/quotes', quoteData),
  updateQuote: (id, quoteData) => api.put(`/quotes/${id}`, quoteData),
  deleteQuote: (id) => api.delete(`/quotes/${id}`),
  exportQuotes: () => api.get('/quotes/export', { responseType: 'blob' }),
};

// Delivery Journal API
export const deliveryJournalAPI = {
  getDeliveryJournal: () => api.get('/delivery-journal'),
  getDeliveryJournalEntry: (id) => api.get(`/delivery-journal/${id}`),
  createDeliveryJournal: (deliveryData) => api.post('/delivery-journal', deliveryData),
  updateDeliveryJournal: (id, deliveryData) => api.put(`/delivery-journal/${id}`, deliveryData),
  deleteDeliveryJournal: (id) => api.delete(`/delivery-journal/${id}`),
};

// Store Invoices API
export const storeInvoicesAPI = {
  getStoreInvoices: () => api.get('/store-invoices'),
  getStoreInvoice: (id) => api.get(`/store-invoices/${id}`),
  createStoreInvoice: (invoiceData) => api.post('/store-invoices', invoiceData),
  updateStoreInvoice: (id, invoiceData) => api.put(`/store-invoices/${id}`, invoiceData),
  deleteStoreInvoice: (id) => api.delete(`/store-invoices/${id}`),
  exportStoreInvoices: () => api.get('/store-invoices/export', { responseType: 'blob' }),
};

// Purchase Receipts API
export const purchaseReceiptsAPI = {
  getPurchaseReceipts: () => api.get('/purchase-receipts'),
  getPurchaseReceipt: (id) => api.get(`/purchase-receipts/${id}`),
  createPurchaseReceipt: (receiptData) => api.post('/purchase-receipts', receiptData),
  updatePurchaseReceipt: (id, receiptData) => api.put(`/purchase-receipts/${id}`, receiptData),
  deletePurchaseReceipt: (id) => api.delete(`/purchase-receipts/${id}`),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  getPurchaseOrders: () => api.get('/purchase-orders'),
  getPurchaseOrder: (id) => api.get(`/purchase-orders/${id}`),
  createPurchaseOrder: (orderData) => api.post('/purchase-orders', orderData),
  updatePurchaseOrder: (id, orderData) => api.put(`/purchase-orders/${id}`, orderData),
  deletePurchaseOrder: (id) => api.delete(`/purchase-orders/${id}`),
};

// Purchase Order Items API
export const purchaseOrderItemsAPI = {
  getPurchaseOrderItems: () => api.get('/purchase-order-items'),
  createPurchaseOrderItem: (itemData) => api.post('/purchase-order-items', itemData),
  updatePurchaseOrderItem: (id, itemData) => api.put(`/purchase-order-items/${id}`, itemData),
  deletePurchaseOrderItem: (id) => api.delete(`/purchase-order-items/${id}`),
};

// Categories API
export const categoriesAPI = {
  getCategories: () => api.get('/categories'),
  createCategory: (data) => api.post('/categories', data),
  updateCategory: (id, data) => api.put(`/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
};

export const paymentsAPI = {
  getPaymentsForRef: (referenceType, referenceId) => api.get(`/payments/ref/${referenceType}/${referenceId}`),
  createPayment: (data) => api.post('/payments', data),
  updatePayment: (id, data) => api.put(`/payments/${id}`, data),
  deletePayment: (id) => api.delete(`/payments/${id}`),
  exportPayments: (params) => api.get('/payments/export', { params, responseType: 'blob' }),
  exportPaymentsJson: (params) => api.get('/payments/export/json', { params }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
};

/** WhatsApp — Green API (envoi immédiat + planification côté serveur) */
export const whatsappAPI = {
  send: (data) => api.post('/whatsapp/send', data),
  sendOrderValidation: (data) => api.post('/whatsapp/send', data),
  getConfig: () => api.get('/whatsapp/config'),
  updateConfig: (data) => api.put('/whatsapp/config', data),
  listScheduled: (params) => api.get('/whatsapp', { params }),
  listSentInstant: (params) => api.get('/whatsapp/sent', { params }),
  schedule: (data) => api.post('/whatsapp/scheduled', data),
  cancelScheduled: (id) => api.delete(`/whatsapp/scheduled/${id}`),
};

export default api; 
export const expensesAPI = {
  list: (params) => api.get('/expenses', { params }),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  remove: (id) => api.delete(`/expenses/${id}`),
};
